<template>
  <client-only>
    <v-card
      dark
      flat
      height="100vh"
      width="100vw"
      class="d-flex justify-end relative"
      tile
    >
      <div v-if="!togglePinEvent" class="pa-3 absolute" style="z-index: 10">
        <v-toolbar tile flat width="320px" prominent>
          <img
            class="d-inline-block pointer mx-auto pt-2"
            style="height: 90px"
            alt="Logo"
            :src="navigator.logo_url"
            @click="$router.push(localePath('/'))"
          />
          <template #extension>
            <v-tabs v-model="tab" grow icons-and-text>
              <v-tabs-slider color="primary"></v-tabs-slider>

              <v-tab> Filtre <v-icon>mdi-filter</v-icon></v-tab>
              <v-tab>
                Semnalează problemă <v-icon>mdi-google-maps</v-icon></v-tab
              >
            </v-tabs>
          </template>
        </v-toolbar>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card
              flat
              tile
              width="320px"
              class="overflow-y-auto mb-5"
              max-height="60vh"
            >
              <v-card-text>
                <v-autocomplete
                  v-model="filters.category"
                  clearable
                  dense
                  class="mb-1"
                  prepend-inner-icon="mdi-alert"
                  hide-details
                  :items="citizenReportsCategories"
                  outlined
                  item-text="name"
                  item-value="name"
                  :label="$t('fields.category')"
                ></v-autocomplete>
                <v-select
                  v-model="filters.status"
                  dense
                  class="mb-1"
                  prepend-inner-icon="mdi-list-status"
                  hide-details
                  :items="['În lucru', 'Rezolvat']"
                  outlined
                  :label="$t('fields.status')"
                >
                </v-select>
                <date-interval-picker
                  scope="citizen-reports"
                  scope-key="created_at"
                ></date-interval-picker>
              </v-card-text>
              <v-card-actions>
                <v-btn color="success" block large text @click="applyFilters">{{
                  $t('buttons.filter')
                }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card
              flat
              tile
              width="320px"
              class="overflow-y-auto mb-5"
              max-height="60vh"
            >
              <v-card-title>Adaugă sesizare</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="problem.last_name"
                  dense
                  class="mb-1"
                  hide-details
                  prepend-inner-icon="mdi-account"
                  outlined
                  :error-messages="errors.last_name"
                  :label="$t('fields.last_name')"
                  @input="clearError('last_name')"
                ></v-text-field>
                <v-text-field
                  v-model="problem.first_name"
                  dense
                  class="mb-1"
                  hide-details
                  :error-messages="errors.first_name"
                  prepend-inner-icon="mdi-account"
                  outlined
                  :label="$t('fields.first_name')"
                  @input="clearError('first_name')"
                ></v-text-field>
                <v-text-field
                  v-model="problem.email"
                  dense
                  class="mb-1"
                  prepend-inner-icon="mdi-email"
                  hide-details
                  :error-messages="errors.email"
                  outlined
                  :label="$t('fields.email')"
                  @input="clearError('email')"
                ></v-text-field>
                <v-text-field
                  v-model="problem.phone"
                  dense
                  class="mb-1"
                  prepend-inner-icon="mdi-phone"
                  hide-details
                  :error-messages="errors.phone"
                  outlined
                  :label="$t('fields.phone')"
                  @input="clearError('phone')"
                ></v-text-field>
                <v-autocomplete
                  v-model="problem.category"
                  clearable
                  dense
                  class="mb-1"
                  :error-messages="errors.category"
                  prepend-inner-icon="mdi-alert"
                  :items="citizenReportsCategories"
                  outlined
                  return-object
                  :hint="$t('citizen_reports.hint')"
                  persistent-hint
                  item-text="name"
                  :label="$t('fields.category')"
                  @input="clearError('category')"
                ></v-autocomplete>
                <v-btn
                  v-if="problem.category"
                  dark
                  block
                  class="mb-1"
                  :color="togglePinEvent ? 'warning' : ''"
                  @click="togglePinEvent = !togglePinEvent"
                >
                  {{ $t('buttons.pin') }}
                  <v-icon class="ml-1">mdi-map-marker</v-icon>
                </v-btn>
                <v-textarea
                  v-model="problem.description"
                  prepend-inner-icon="mdi-card-text"
                  outlined
                  clearable
                  auto-grow
                  :label="$t('fields.description')"
                  rows="5"
                  class="mb-1"
                  @input="clearError('description')"
                ></v-textarea>
                <image-cropper
                  :ratio="null"
                  class="mb-1"
                  :hint="$t('petitions.image_hint')"
                  is-edit
                  :dense="true"
                  :label="$t('fields.image')"
                  @image-ready="problem.image = $event"
                ></image-cropper>
                <v-checkbox
                  v-model="problem.tos"
                  class="mb-1"
                  :label="$t('fields.tos')"
                  append-icon="mdi-open-in-new"
                  @click:append="goToWidgetLink(null, null, $route.query.tos, 1)"
                ></v-checkbox>
              </v-card-text>
              <v-card-text>
                <v-btn
                  block
                  color="success"
                  :loading="loading"
                  :disabled="loading || !problem.tos"
                  @click="send"
                  >Trimite <v-icon class="ml-1">mdi-send</v-icon></v-btn
                >
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </div>
      <div id="map" style="height: 100%; width: 100%"></div>
    </v-card>
  </client-only>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import { mapActions, mapMutations, mapState } from 'vuex'
import DateIntervalPicker from '~/components/common/filters/DateIntervalPicker'
import ImageCropper from '~/components/common/forms/ImageCropper'
import goToWidgetLink from '~/mixins/goToWidgetLink'

import 'mapbox-gl/dist/mapbox-gl.css'
import 'mapbox-gl-controls/lib/controls.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

export default {
  name: 'ProblemReports',
  components: { DateIntervalPicker, ImageCropper },
  mixins: [goToWidgetLink],
  beforeRouteLeave(to, from, next) {
    this.$recaptcha.destroy()
    this.resetScopeFilters({ scope: 'citizen-reports' })

    next()
  },
  layout: 'map',
  data() {
    return {
      tab: null,
      filters: {
        categories: [],
        category: [],
        status: '',
      },
      problem: {},
      togglePinEvent: false,
      map: null,
      coords: [],
      loading: false,
      markers: [],
      accessToken:
        'pk.eyJ1IjoiZ3JlYmUxOTkxIiwiYSI6ImNrcjF3MDl0ejI1bm4ycW82dHM1Mzl1cmMifQ.bdG78NAiFuxu2omfuTyouQ',
    }
  },
  async fetch() {
    await this.setItemsPerPage({
      scope: 'citizen-reports-categories',
      itemsPerPage: 999,
    })

    await this.createResource('navigators')
    await this.getData('citizen-reports-categories')
  },
  computed: {
    ...mapState('navigators', ['navigator']),
    ...mapState('citizenReportsCategories', ['citizenReportsCategories']),
    ...mapState('errors', ['errors']),
    ...mapState('citizenReports', ['citizenReports']),
  },
  async created() {
    if (process.client) {
      this.getUserLocation(false)
    }

    try {
      await this.$recaptcha.init()
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    ...mapMutations('errors', ['clearError']),
    ...mapActions('resources', ['createResource', 'getData', 'storeResource']),
    ...mapActions('query', ['resetScopeFilters']),
    ...mapMutations('query', ['setItemsPerPage', 'setFilter']),
    async applyFilters() {
      this.setFilter({
        scope: 'citizen-reports',
        key: 'filter[category]',
        val: this.filters.category,
      })

      this.setFilter({
        scope: 'citizen-reports',
        key: 'filter[status]',
        val: this.filters.status,
      })

      await this.getData('citizen-reports')

      this.map = null
      this.createMap()
    },
    async send() {
      const data = {
        ...this.problem,
        category: this.problem.category.name,
      }

      if (this.markers.length) {
        data.marker = {
          ...this.markers[0].marker.getLngLat(),
          icon: this.problem.category.icon,
        }
      }

      this.loading = true
      const token = await this.$recaptcha.execute('send_citizen_report')

      await this.storeResource({
        scope: 'citizen-reports',
        data: { ...data, token },
        navigate: false,
        isFormData: true,
      })

      this.loading = false
    },
    async getUserLocation(satelliteMap = false) {
      await this.$nextTick()

      const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const crd = pos.coords
          this.coords = [crd.longitude, crd.latitude]
          this.createMap(satelliteMap)
        },
        (err) => this.$errorAlert(err.message),
        options
      )
    },
    createMap() {
      if (!mapboxgl.supported()) {
        this.$errorAlert(
          'Browserul dumneavoastra nu suporta functionalitatile necesare pentru randarea hărții'
        )
        return
      }

      mapboxgl.accessToken = this.accessToken
      this.map = new mapboxgl.Map({
        accessToken: this.accessToken,
        container: 'map',
        style: 'mapbox://styles/mapbox/streets-v11?optimize=true',
        zoom: 14,
        center: this.coords,
      })

      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left')
      this.map.addControl(
        new this.$stylesControl({
          styles: [
            {
              label: 'Satelit',
              styleName: 'Satellite',
              styleUrl: 'mapbox://styles/mapbox/satellite-v9?optimize=true',
            },
            {
              label: 'Oraș',
              styleName: 'Outdoors',
              styleUrl: 'mapbox://styles/mapbox/outdoors-v11?optimize=true',
            },
          ],
        }),
        'bottom-left'
      )

      this.map.on('load', () => {
        this.addNewMarkers()
      })

      this.map.on('click', (e) => {
        if (!this.togglePinEvent) {
          return
        }

        this.coords = [e.lngLat.wrap().lng, e.lngLat.wrap().lat]
        this.addNewMarker()
        this.togglePinEvent = !this.togglePinEvent
      })
    },
    addNewMarkers() {
      this.citizenReports.forEach((report) => {
        this.addMarker(report)
      })
    },
    addNewMarker() {
      if (this.markers.length) {
        this.markers[0].marker.remove()
        this.markers.length = 0
      }

      const container = document.createElement('div')
      const border = document.createElement('div')
      const el = document.createElement('i')
      el.className =
        'marker v-icon notranslate mdi ' + this.problem.category.icon
      el.style.fontSize = `1.5rem`
      el.style.color = `white`
      el.style.transition = 'none'
      el.style.transform = 'rotate(-45deg)'
      border.style.backgroundColor = 'red'
      border.style.borderTopLeftRadius = '50%'
      border.style.borderTopRightRadius = '50%'
      border.style.borderBottomLeftRadius = '50%'
      border.style.transform = 'rotate(45deg)'
      border.style.border = '2px solid white'
      border.style.padding = '5px'
      container.appendChild(border)
      border.appendChild(el)

      const marker = new mapboxgl.Marker(container)
        .setLngLat(this.coords)
        .addTo(this.map)

      this.markers.push({ marker })
    },
    addMarker(report) {
      if (!report.marker) return
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
      }).setHTML(
        `
            <div style="display: flex; flex-direction: column; gap: 5px">
              <div style="color: black; font-size: 0.9rem"><b>Categorie:</b> ${
                report.category
              }</div>
              <div style="color: black; font-size: 0.9rem"><b>Stare:</b> ${
                report.status
              }</div>
              <div style="color: black; font-size: 0.9rem"><b>Descriere:</b> ${
                report.description
              }</div>
              <div style="color: black; font-size: 0.9rem"><b>Creat la:</b> ${this.$options.filters.formatDate(
                report.created_at
              )}</div>
            </div>
          `
      )

      const container = document.createElement('div')
      const border = document.createElement('div')
      const el = document.createElement('i')
      el.className = 'marker v-icon notranslate mdi ' + report.marker.icon
      el.style.fontSize = `1.5rem`
      el.style.color = `white`
      el.style.transition = 'none'
      el.style.transform = 'rotate(-45deg)'
      border.style.backgroundColor = 'red'
      border.style.borderTopLeftRadius = '50%'
      border.style.borderTopRightRadius = '50%'
      border.style.borderBottomLeftRadius = '50%'
      border.style.transform = 'rotate(45deg)'
      border.style.border = '2px solid white'
      border.style.padding = '5px'
      container.appendChild(border)
      border.appendChild(el)

      new mapboxgl.Marker(container)
        .setLngLat([report.marker.lng, report.marker.lat])
        .addTo(this.map)
        .setPopup(popup)
    },
  },
}
</script>

<style>
.mapboxgl-ctrl button {
  color: black;
}
</style>
